import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useBrandHistory from "../../../hooks/useBrandHistory";
import { useTimeUtil } from "../../../hooks/useTimeUtil";
import "./History.css";
import refreshIcon from "../../../assets/images/refresh.svg";
import { ContentLog } from "../../../types/dashboard";

import Shimmer from "../../shimmer/Shimmer";

interface HistoryProps {
  brandId: string;
  onViewItem?: (item: ContentLog) => void;
  onAnalyticsItem?: (item: ContentLog) => void;
  onDeleteItem?: (item: ContentLog) => Promise<boolean>;
}

const History: React.FC<HistoryProps> = ({
  brandId,
  onViewItem,
  onAnalyticsItem,
  onDeleteItem,
}) => {
  const {
    history,
    refreshHistory,
    loading,
    isReconciling,
    page,
    totalPages,
    totalLogs,
    goToPrevPage,
    goToNextPage,
    getIconForPlatform,
  } = useBrandHistory(brandId);
  const { formatDate } = useTimeUtil();
  const [openActionId, setOpenActionId] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [reconcileStepIndex, setReconcileStepIndex] = React.useState(0);

  React.useEffect(() => {
    if (!isReconciling) {
      setReconcileStepIndex(0);
      return;
    }

    const stepTimers = [8000, 22000, 40000].map((delay, index) =>
      window.setTimeout(() => {
        setReconcileStepIndex(index + 1);
      }, delay),
    );

    return () => {
      stepTimers.forEach(window.clearTimeout);
    };
  }, [isReconciling]);

  const handleDelete = React.useCallback(
    async (item: ContentLog) => {
      if (!onDeleteItem) return;
      setDeletingId(item.id);
      const success = await onDeleteItem(item);
      setDeletingId(null);
      setOpenActionId(null);
      if (success) {
        refreshHistory();
      }
    },
    [onDeleteItem, refreshHistory],
  );

  const contentLogsShimmer = () => {
    return (
      <table className="history-table">
        <thead>
          <tr>
            <th>
              <Shimmer width={60} />
            </th>
            <th>
              <Shimmer width={80} />
            </th>
            <th>
              <Shimmer width={60} />
            </th>
            <th>
              <Shimmer width={60} />
            </th>
            <th>
              <Shimmer width={40} />
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 3 }).map((_, i) => (
            <tr key={i}>
              <td>
                <Shimmer width={120} />
              </td>
              <td>
                <Shimmer width={180} />
              </td>
              <td>
                <Shimmer width={80} />
              </td>
              <td>
                <Shimmer width={100} />
              </td>
              <td>
                <Shimmer width={40} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const reconcileProgress = () => {
    const steps = [
      "Checking Post results",
      "Matching platform posts",
      "Updating statuses and dates",
      "Refreshing history",
    ];

    return (
      <div className="history-reconcile-panel" role="status" aria-live="polite">
        <div className="history-reconcile-spinner" />
        <div>
          <h3>Syncing post status</h3>
          <p>
            We are checking the posts and updating scheduled, published, and
            failed posts.
          </p>
        </div>
        <div className="history-reconcile-steps">
          {steps.map((step, index) => (
            <div className="history-reconcile-step" key={step}>
              <span
                className={
                  index < reconcileStepIndex
                    ? "history-reconcile-dot history-reconcile-dot-complete"
                    : index === reconcileStepIndex
                    ? "history-reconcile-dot history-reconcile-dot-active"
                    : "history-reconcile-dot history-reconcile-dot-pending"
                }
              />
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="history-tab">
      <div className="history-container" style={{ marginTop: 0 }}>
        <div
          className="history-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>History</span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {totalLogs > 0 && totalPages > 0 && (
              <div
                className="history-pagination"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <button
                  type="button"
                  onClick={goToPrevPage}
                  disabled={page <= 1 || loading}
                  aria-label="Previous page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    background: page <= 1 || loading ? "#f9fafb" : "#fff",
                    color: page <= 1 || loading ? "#9ca3af" : "#374151",
                    cursor: page <= 1 || loading ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {page} / {totalPages}
                  {totalLogs > 0 && (
                    <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                      {" "}
                      {/* · {totalLogs} */}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={goToNextPage}
                  disabled={page >= totalPages || loading}
                  aria-label="Next page"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    background:
                      page >= totalPages || loading ? "#f9fafb" : "#fff",
                    color:
                      page >= totalPages || loading ? "#9ca3af" : "#374151",
                    cursor:
                      page >= totalPages || loading ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
            <button
              onClick={refreshHistory}
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                padding: "4px",
                marginLeft: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f3f4f6")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              title="Refresh History"
            >
              <img
                src={refreshIcon}
                alt="Refresh"
                className={isReconciling ? "history-refresh-icon-spinning" : ""}
                style={{ width: "14px", height: "14px" }}
              />
            </button>
          </div>
        </div>
        {isReconciling ? (
          reconcileProgress()
        ) : loading ? (
          contentLogsShimmer()
        ) : totalLogs === 0 ? (
          <div style={{ padding: "20px", color: "#6b7280" }}>
            No history found for this brand.
          </div>
        ) : (
          <>
            <table className="history-table">
              <thead>
                <tr>
                  <th>BRAND</th>
                  <th style={{ width: "40%" }}>TOPIC</th>
                  <th style={{ textAlign: "center" }}>PLATFORMS</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {history.map((gen) => (
                  <tr key={gen.id}>
                    <td style={{ fontWeight: 500 }}>{gen.brand_name}</td>
                    <td style={{ fontWeight: 600, color: "#1f2937" }}>
                      {gen.topic}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                        }}
                      >
                        {gen.content.platforms?.map((p) => {
                          const icon = getIconForPlatform(p);
                          return icon ? (
                            <img
                              key={p}
                              src={icon}
                              alt={p}
                              style={{ width: "18px", height: "18px" }}
                            />
                          ) : null;
                        })}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`history-badge history-badge-${gen.status
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {gen.status.charAt(0).toUpperCase() +
                          gen.status.slice(1).toLowerCase()}
                      </span>
                      {gen.content?.pfm_result?.failure_reason && (
                        <div
                          style={{
                            marginTop: "4px",
                            color: "#b91c1c",
                            fontSize: "12px",
                            lineHeight: 1.4,
                            maxWidth: "220px",
                          }}
                        >
                          {gen.content.pfm_result.failure_reason}
                        </div>
                      )}
                    </td>
                    <td style={{ color: "#6B7280" }}>
                      {formatDate(gen.created_at, "YYYY-MM-DD HH:mm:ss")}
                    </td>
                    <td>
                      {gen.status?.toUpperCase() === "PUBLISHED" &&
                      gen.content?.provider === "postforme" &&
                      gen.post_id ? (
                        <span
                          className="history-view-action"
                          style={{ color: "#7c3aed" }}
                          onClick={() => onAnalyticsItem?.(gen)}
                        >
                          Analytics
                        </span>
                      ) : (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <span
                            className="history-view-action"
                            onClick={() =>
                              setOpenActionId((current) =>
                                current === gen.id ? null : gen.id,
                              )
                            }
                          >
                            Manage
                          </span>
                          {openActionId === gen.id && (
                            <div
                              style={{
                                position: "absolute",
                                top: "calc(100% + 6px)",
                                right: 0,
                                minWidth: "120px",
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                                zIndex: 10,
                                overflow: "hidden",
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenActionId(null);
                                  onViewItem?.(gen);
                                }}
                                style={{
                                  width: "100%",
                                  textAlign: "left",
                                  border: "none",
                                  background: "#fff",
                                  padding: "10px 12px",
                                  cursor: "pointer",
                                  color: "#1f2937",
                                  fontSize: "13px",
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={deletingId === gen.id}
                                onClick={() => handleDelete(gen)}
                                style={{
                                  width: "100%",
                                  textAlign: "left",
                                  border: "none",
                                  background: "#fff",
                                  padding: "10px 12px",
                                  cursor:
                                    deletingId === gen.id
                                      ? "not-allowed"
                                      : "pointer",
                                  color: "#b91c1c",
                                  fontSize: "13px",
                                }}
                              >
                                {deletingId === gen.id
                                  ? "Deleting..."
                                  : "Delete"}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default History;
